<template>
  <div class="page-root">
    <h1>
      <span class="font-weight-bold" style="font-family: maplestory"> 펫 설정 </span>
    </h1>

    <div class="elevation-1 pa-4 mt-4" style="background-color: var(--v-surface-base); border-radius: 10px">
      <!-- TITLE -->
      <h3 class="d-flex">
        <p class="primary--text mr-1" style="font-weight: 900">|</p>
        <p>버츄펫 설정</p>
      </h3>

      <!-- BODY -->
      <div class="d-flex justify-start">
        <div class="mx-4 flex-shrink-0" style="width: 200px; height: 200px; border-radius: 20px; overflow: hidden">
          <ModelFacade style="transform: scale(0.5) translate(-50%, -50%)" ref="modelFacade" :size="400"></ModelFacade>
        </div>
        <div style="width: 630px">
          <!-- 펫 프로필 사진 (펫 이름 위) -->
          <div class="d-flex align-center mb-4 px-1">
            <v-avatar size="56" class="mr-3">
              <v-img :src="petPreviewUrl" alt="pet-profile" />
            </v-avatar>
            <div>
              <div class="text-subtitle-2 font-weight-bold">펫 프로필 사진</div>
              <v-btn color="primary" small outlined class="mt-1" @click="openPetProfileDialog">
                프로필 사진 변경
              </v-btn>
            </div>
          </div>

          <v-expansion-panels v-model="tempFlags" multiple>
            <!-- 펫 이름 -->
            <v-expansion-panel>
              <v-expansion-panel-header> 펫 이름 </v-expansion-panel-header>
              <v-expansion-panel-content>
                <!-- 펫 이름 변경 막아둠 — 현재 이름은 읽기 전용으로 표시 -->
                <v-text-field
                  v-model="petName"
                  placeholder="펫 이름"
                  dense
                  hide-details
                  outlined
                  readonly
                  disabled
                ></v-text-field>
                <p class="text-caption grey--text mt-2 mb-0">펫 이름은 현재 변경할 수 없습니다.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- 배경설정·좌우반전은 /my/broadcast(LeftController)로 이동 — DB + 소켓 라이브 -->

            <!-- <v-expansion-panel>
          <v-expansion-panel-header> 조명설정 </v-expansion-panel-header>
          <v-expansion-panel-content> 카툰, 실사화 </v-expansion-panel-content>
        </v-expansion-panel> -->
          </v-expansion-panels>
        </div>
      </div>
    </div>

    <!-- 펫 프로필 사진 변경 다이얼로그 (유저 프로필과 동일 방식) -->
    <base-dialog v-model="isShowPetProfileDialog" title="펫 프로필 사진 변경" :max-width="420" persistent>
      <div class="d-flex justify-center pb-2">
        <v-avatar size="88">
          <v-img :src="petDialogPreviewUrl" alt="preview" />
        </v-avatar>
      </div>

      <v-tabs v-model="petProfileTab" grow color="primary lighten-1" class="mb-3">
        <v-tab>URL 입력</v-tab>
        <v-tab>업로드</v-tab>
      </v-tabs>

      <v-tabs-items v-model="petProfileTab">
        <!-- URL 입력 (외부 이미지) -->
        <v-tab-item>
          <v-text-field
            v-model="petProfileImageUrl"
            outlined
            dense
            hide-details="auto"
            placeholder="https://..."
            hint="https:// 로 시작하는 외부 이미지 주소"
            persistent-hint
          ></v-text-field>
        </v-tab-item>

        <!-- 업로드 (R2 미구현 → 파일 첨부만) -->
        <v-tab-item>
          <v-file-input
            v-model="petProfileFile"
            accept="image/*"
            outlined
            dense
            prepend-icon="mdi-camera"
            hide-details="auto"
            label="업로드"
          ></v-file-input>
          <p class="text-caption grey--text mt-2 mb-0">이미지를 자동으로 작게 리사이즈해 업로드합니다.</p>
        </v-tab-item>
      </v-tabs-items>

      <template #actions>
        <v-btn text @click="isShowPetProfileDialog = false">취소</v-btn>
        <v-btn color="primary" :loading="isSavingPetProfile" :disabled="!canSavePetProfile" @click="onSavePetProfileImage">
          저장
        </v-btn>
      </template>
    </base-dialog>
  </div>
</template>

<script>
import SupabaseApi from "@/api/SupabaseApi";
import delay from "@/utils/delay";
import { uploadProfileImage } from "@/utils/profileImage";

export default {
  async created() {
    await this.initModel();
    await this.initBroadcastSetting();
    await this.initPetName();
  },
  mounted() {},
  data() {
    return {
      tempFlags: [0, 1, 2],

      petName: "",

      // 펫 프로필 사진
      petImageUrlRaw: null, // pet_profile.image_url
      petModelIndex: null, // 폴백 썸네일용 model_url
      isShowPetProfileDialog: false,
      petProfileTab: 0, // 0: URL 입력 / 1: 업로드
      petProfileImageUrl: "",
      petProfileFile: null,
      petProfileFilePreview: null, // 선택 파일 로컬 미리보기 objectURL
      isSavingPetProfile: false,
    };
  },
  watch: {
    // 선택 파일 → 로컬 미리보기 objectURL (이전 것 해제)
    petProfileFile(file) {
      if (this.petProfileFilePreview) URL.revokeObjectURL(this.petProfileFilePreview);
      this.petProfileFilePreview = file ? URL.createObjectURL(file) : null;
    },
  },
  computed: {
    // 저장 가능: URL 탭은 https URL, 업로드 탭은 파일 선택 시
    canSavePetProfile() {
      return this.petProfileTab === 1 ? !!this.petProfileFile : (this.petProfileImageUrl || "").startsWith("https://");
    },
    // 펫 이미지: image_url 우선, 없으면 model_url 동물 key → 폴백 썸네일 (사이드바와 동일 규칙)
    petImageFromModel() {
      const url = this.petModelIndex;
      if (!url) return null;
      const file = url
        .split("/")
        .pop()
        .replace(/\.(glb|gltf)$/i, "");
      const key = file.split("+")[0].split("_v")[0];
      return key ? `/pet/profile-thumbnail-fallback/${key}_thumbnail.png` : null;
    },
    // 페이지 프리뷰(저장된 값 기준)
    petPreviewUrl() {
      return this.petImageUrlRaw || this.petImageFromModel;
    },
    // 다이얼로그 프리뷰: 업로드 탭은 선택 파일, URL 탭은 입력값, 없으면 저장된 값
    petDialogPreviewUrl() {
      if (this.petProfileTab === 1 && this.petProfileFilePreview) return this.petProfileFilePreview;
      return this.petProfileImageUrl || this.petPreviewUrl;
    },
  },
  methods: {
    async initModel() {
      const modelIndex = await SupabaseApi.PetProfile.fetchModelIndex();
      await this.$refs.modelFacade.init(modelIndex);

      await delay(700);
      await this.$refs.modelFacade.startIdle("action-only--{{loop}}idle-40");

      return modelIndex;
    },
    async initPetName() {
      const profile = await SupabaseApi.PetProfile.fetchProfile();
      this.petName = (profile && profile.name) || "";
      this._originalPetName = this.petName; // 변경 없을 때 중복체크 스킵용
      this.petImageUrlRaw = (profile && profile.imageUrl) || null;
      this.petModelIndex = (profile && profile.modelUrl) || null;
    },
    // 펫 프로필 사진 — 다이얼로그 열기(저장된 https 값으로 프리필) / URL 저장
    openPetProfileDialog() {
      const cur = this.petImageUrlRaw || "";
      this.petProfileImageUrl = cur.startsWith("https://") ? cur : "";
      this.petProfileFile = null;
      this.petProfileTab = 0;
      this.isShowPetProfileDialog = true;
    },
    async onSavePetProfileImage() {
      this.isSavingPetProfile = true;
      try {
        let url;
        if (this.petProfileTab === 1) {
          // 업로드 탭: 리사이즈 + 해시 파일명으로 R2 업로드
          if (!this.petProfileFile) return;
          url = await uploadProfileImage(this.petProfileFile, "pet");
        } else {
          // URL 탭: 외부 https 주소
          url = (this.petProfileImageUrl || "").trim();
          if (!url.startsWith("https://")) {
            if (this.$notify)
              this.$notify({ type: "warning", title: "https:// 로 시작하는 URL을 입력해주세요.", duration: 2400 });
            return;
          }
        }
        await SupabaseApi.PetProfile.updateProfile({ imageUrl: url });
        this.petImageUrlRaw = url;
        this.$store.commit("__bumpPetRevision"); // 사이드바 펫 이미지 즉시 갱신
        this.isShowPetProfileDialog = false;
        if (this.$notify) this.$notify({ type: "success", title: "펫 프로필 사진을 변경했어요.", duration: 1500 });
      } catch (error) {
        console.error(error);
        if (this.$notify) this.$notify({ type: "error", title: "펫 프로필 사진 변경에 실패했습니다.", duration: 2400 });
      } finally {
        this.isSavingPetProfile = false;
      }
    },
    async onSavePetName() {
      const name = this.petName.trim();
      if (!name) return;
      try {
        // 이름 변경 시에만 글로벌 중복체크(대소문자무시+trim). 동일 이름이면 스킵.
        const unchanged = name.toLowerCase() === (this._originalPetName || "").trim().toLowerCase();
        if (!unchanged) {
          const ok = await SupabaseApi.PetProfile.isNameAvailable(name);
          if (!ok) {
            alert("이미 사용 중인 이름이에요. 다른 이름을 입력해주세요.");
            return;
          }
        }
        await SupabaseApi.PetProfile.updateProfile({ name });
        this._originalPetName = name;
        this.$store.commit("__setVirtupetName", name); // 사이드바 등 즉시 반영
        if (this.$notify) this.$notify({ type: "success", title: "펫 이름을 저장했어요.", duration: 1500 });
      } catch (e) {
        if (e && e.message === "E_PET_NAME_TAKEN") alert("이미 사용 중인 이름이에요. 다른 이름을 입력해주세요.");
        else {
          console.error(e);
          alert("저장에 실패했어요.");
        }
      }
    },
    async initBroadcastSetting() {
      const broadcastSetting = await SupabaseApi.PetBroadcastSetting.fetchBroadcastSetting();
      if (!broadcastSetting) return;

      // 배경·좌우반전 제어는 /my/broadcast로 이동. 여기선 모델 미리보기에만 반영.
      const { isReverseModel, backgroundSetting } = broadcastSetting;
      const bg = backgroundSetting || {};
      this.$refs.modelFacade.changeModelReverse(isReverseModel || false);
      this.$refs.modelFacade.changeBackgroundCanvas(bg.visible !== false ? bg.file_name : null);
    },

  },
};
</script>

<style></style>
